import ApiService from './apiService';

/**
 * Service pour gérer les opérations liées aux clients
 * 
 */
class CustomerService {
    /**
     * Récupérer la liste des clients avec filtres et pagination
     * @param {Object} params Paramètres de filtrage et pagination
     * @returns {Promise} Réponse de l'API avec la liste des clients
     */
    getCustomers(params = {}) {
        return ApiService.get('/customers', params);
    }

    /**
     * Récupérer un client par son ID
     * @param {Number} id ID du client
     * @param {Object} params Paramètres additionnels (relations à charger)
     * @returns {Promise} Réponse de l'API avec les détails du client
     */
    getCustomer(id, params = {}) {
        return ApiService.get(`/customers/${id}`, params);
    }

    /**
     * Créer un nouveau client
     * @param {Object} customerData Données du client à créer
     * @returns {Promise} Réponse de l'API avec le client créé
     */
    createCustomer(customerData) {
        return ApiService.post('/customers', customerData);
    }

    /**
     * Mettre à jour un client existant
     * @param {Number} id ID du client à mettre à jour
     * @param {Object} customerData Nouvelles données du client
     * @returns {Promise} Réponse de l'API avec le client mis à jour
     */
    updateCustomer(id, customerData) {
        return ApiService.put(`/customers/${id}`, customerData);
    }

    /**
     * Supprimer un client
     * @param {Number} id ID du client à supprimer
     * @returns {Promise} Réponse de l'API
     */
    deleteCustomer(id) {
        return ApiService.delete(`/customers/${id}`);
    }

    /**
     * Activer un client
     * @param {Number} id ID du client à activer
     * @returns {Promise} Réponse de l'API avec le client activé
     */
    activateCustomer(id) {
        return ApiService.post(`/customers/${id}/activate`);
    }

    /**
     * Désactiver un client
     * @param {Number} id ID du client à désactiver
     * @returns {Promise} Réponse de l'API avec le client désactivé
     */
    deactivateCustomer(id) {
        return ApiService.post(`/customers/${id}/deactivate`);
    }

    /**
     * Récupérer le portefeuille d'un client avec ses transactions
     * @param {Number} id ID du client
     * @returns {Promise} Réponse de l'API avec les informations du portefeuille
     */
    getCustomerWallet(id) {
        return ApiService.get(`/customers/${id}/wallet`);
    }

    /**
     * Créditer le portefeuille d'un client
     * @param {Number} id ID du client
     * @param {Object} data Données de crédit
     * @param {Number} data.amount Montant à créditer (requis, min: 0.01)
     * @param {String} [data.description] Description de la transaction
     * @returns {Promise} Réponse de l'API avec le portefeuille mis à jour
     */
    creditCustomerWallet(id, data) {
        return ApiService.post(`/customers/${id}/wallet/credit`, data);
    }

    /**
     * Débiter le portefeuille d'un client
     * @param {Number} id ID du client
     * @param {Object} data Données de débit
     * @param {Number} data.amount Montant à débiter (requis, min: 0.01)
     * @param {String} [data.description] Description de la transaction
     * @returns {Promise} Réponse de l'API avec le portefeuille mis à jour
     */
    debitCustomerWallet(id, data) {
        return ApiService.post(`/customers/${id}/wallet/debit`, data);
    }

    /**
     * Récupérer les ventes d'un client
     * @param {Number} id ID du client
     * @param {Object} params Paramètres additionnels
     * @returns {Promise} Réponse de l'API avec les ventes du client
     */
    getCustomerSales(id, params = {}) {
        return ApiService.get(`/customers/${id}/sales`, params);
    }

    /**
     * Rechercher des clients
     * @param {String} query Terme de recherche
     * @param {Object} options Options additionnelles
     * @returns {Promise} Réponse de l'API avec les résultats de recherche
     */
    searchCustomers(query, options = {}) {
        return ApiService.get('/customers', {
            search: query,
            ...options
        });
    }

    /**
     * Récupérer une liste simple des clients pour les sélecteurs
     * @param {Object} params Paramètres additionnels
     * @returns {Promise} Réponse de l'API avec une liste simplifiée
     */
    getCustomersForSelect(params = {}) {
        return ApiService.get('/customers', {
            per_page: 100,
            select: 'id,name,phone,email',
            ...params
        });
    }

    /**
     * Télécharger la liste des clients non payés
     * @param {boolean} open Si true, ouvre le fichier dans un nouvel onglet au lieu de le télécharger
     * @returns {Promise} Télécharge ou ouvre le fichier
     */
    async downloadUnpaidList(open = false) {
        if (open) {
            // Pour l'ouverture, utiliser axios directement avec les intercepteurs configurés
            const response = await ApiService.get('/customers/download-unpaid-list', {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
            
            // Nettoyer l'URL après un délai pour permettre l'ouverture
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
        } else {
            // Pour le téléchargement, utiliser ApiService.download
            await ApiService.download('/customers/download-unpaid-list', {}, 'clients-non-payes.pdf');
        }
    }

    /**
     * Formater un client pour l'affichage
     * @param {Object} customer Données du client
     * @returns {Object} Client formaté
     */
    formatCustomerForDisplay(customer) {
        if (!customer) return null;

        return {
            ...customer,
            formattedAddress: this.formatAddress(customer),
            salesCount: customer.sales_count || 0,
            totalSpent: customer.total_spent || 0
        };
    }

    /**
     * Formater l'adresse d'un client
     * @param {Object} customer Données du client
     * @returns {String} Adresse formatée
     */
    formatAddress(customer) {
        if (!customer) return '';

        const parts = [
            customer.address,
            customer.city,
            customer.state,
            customer.zip
        ].filter(part => part);

        return parts.join(', ');
    }
}

export default new CustomerService();